// src/pages/sessions/components/LiveSession/mobile/PhoneLayout.jsx
import React, { useState, useCallback } from 'react';
import { RoomAudioRenderer } from '@livekit/components-react';
import PhoneVideoArea from './PhoneVideoArea';
import PhoneControlsOverlay from './PhoneControlsOverlay';
import PhoneBottomNav from './PhoneBottomNav';
import PhonePanel from './PhonePanel';

const PhoneLayout = ({
  sessionId,
  currentUser,
  isTeacher,
  screenSharers,
  embeddedDocument,
  documentSharer,
  localParticipantIdentity,
  virtualRooms,
  participantVirtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  remoteTeacherStates,
  currentVirtualRoom,
  hideOwnVideo,
  gracePeriodParticipants,
  isWhiteboardActive,
  isWhiteboardDetached,
  whiteboardManager,
  audioEnabled,
  videoEnabled,
  userMutedAudio,
  userMutedVideo,
  hasUserInteracted,
  canToggleMicrophone,
  canToggleCamera,
  onToggleAudio,
  onToggleVideo,
  onRotateCamera,
  onLeave,
  isAllBroadcasting,
  isAllMonitoring,
  isAllMonitoringSilent,
  onCreateRoom,
  onDeleteRoom,
  onRenameRoom,
  onAssignParticipant,
  onToggleBroadcast,
  onToggleMonitor,
  onToggleMonitorSilent,
  onToggleAllBroadcast,
  onToggleAllMonitor,
  onToggleAllMonitorSilent,
  onCreateIndividualRooms,
}) => {
  const [activeView, setActiveView] = useState('video');
  const [unreadMessages, setUnreadMessages] = useState(0);

  const handleViewChange = useCallback((view) => {
    setActiveView(view);
  }, []);

  return (
    <div
      className="bg-gray-900 flex flex-col overflow-hidden"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Video area — flex-1, fills space above controls and bottom nav */}
      <PhoneVideoArea
        screenSharers={screenSharers}
        embeddedDocument={embeddedDocument}
        documentSharer={documentSharer}
        localParticipantIdentity={localParticipantIdentity}
        virtualRooms={virtualRooms}
        participantVirtualRooms={participantVirtualRooms}
        teacherBroadcastRooms={teacherBroadcastRooms}
        teacherMonitorRooms={teacherMonitorRooms}
        teacherMonitorSilentRooms={teacherMonitorSilentRooms}
        remoteTeacherStates={remoteTeacherStates}
        currentVirtualRoom={currentVirtualRoom}
        isTeacher={isTeacher}
        hideOwnVideo={hideOwnVideo}
        gracePeriodParticipants={gracePeriodParticipants}
        isWhiteboardActive={isWhiteboardActive}
        isWhiteboardDetached={isWhiteboardDetached}
        whiteboardManager={whiteboardManager}
      />

      {/* Controls bar — in normal flex flow, never overlaps video */}
      {activeView === 'video' && (
        <PhoneControlsOverlay
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          userMutedAudio={userMutedAudio}
          userMutedVideo={userMutedVideo}
          hasUserInteracted={hasUserInteracted}
          canToggleMicrophone={canToggleMicrophone}
          canToggleCamera={canToggleCamera}
          onToggleAudio={onToggleAudio}
          onToggleVideo={onToggleVideo}
          onRotateCamera={onRotateCamera}
          onLeave={onLeave}
        />
      )}

      {/* Slide-up panels */}
      <PhonePanel
        activeView={activeView}
        onViewChange={handleViewChange}
        sessionId={sessionId}
        currentUser={currentUser}
        onUnreadCountChange={setUnreadMessages}
        participantVirtualRooms={participantVirtualRooms}
        virtualRooms={virtualRooms}
        teacherBroadcastRooms={teacherBroadcastRooms}
        teacherMonitorRooms={teacherMonitorRooms}
        teacherMonitorSilentRooms={teacherMonitorSilentRooms}
        isTeacher={isTeacher}
        isAllBroadcasting={isAllBroadcasting}
        isAllMonitoring={isAllMonitoring}
        isAllMonitoringSilent={isAllMonitoringSilent}
        onCreateRoom={onCreateRoom}
        onDeleteRoom={onDeleteRoom}
        onRenameRoom={onRenameRoom}
        onAssignParticipant={onAssignParticipant}
        onToggleBroadcast={onToggleBroadcast}
        onToggleMonitor={onToggleMonitor}
        onToggleMonitorSilent={onToggleMonitorSilent}
        onToggleAllBroadcast={onToggleAllBroadcast}
        onToggleAllMonitor={onToggleAllMonitor}
        onToggleAllMonitorSilent={onToggleAllMonitorSilent}
        onCreateIndividualRooms={onCreateIndividualRooms}
      />

      {/* Bottom navigation */}
      <PhoneBottomNav
        activeView={activeView}
        onViewChange={handleViewChange}
        isTeacher={isTeacher}
        hasUnreadMessages={unreadMessages > 0}
        unreadCount={unreadMessages}
        isWhiteboardActive={isWhiteboardActive}
      />

      <RoomAudioRenderer />
    </div>
  );
};

export default PhoneLayout;
